/** The scanner's filters and funnel arithmetic (U021, U022); an absent metric never matches a filter. */
import type { FunnelRow, FunnelStage, ScanRow, Side } from "../../contracts/views.ts";

export interface ScanFilters {
  readonly side: "ALL" | Side;
  readonly stage: "ALL" | FunnelStage;
  readonly sector: string;
  readonly catalyst: "ANY" | "WITH" | "WITHOUT";
  /** Minimum turnover in whole rupees (liquidity). */
  readonly minTurnoverRupees: bigint | null;
  /** Maximum participation in whole percent. */
  readonly maxParticipationPercent: number | null;
  /** Maximum absolute gap in basis points. */
  readonly maxAbsGapBp: number | null;
}

export const NO_FILTERS: ScanFilters = {
  side: "ALL",
  stage: "ALL",
  sector: "ALL",
  catalyst: "ANY",
  minTurnoverRupees: null,
  maxParticipationPercent: null,
  maxAbsGapBp: null,
};

export type Parsed = { readonly ok: true; readonly value: number | null } | { readonly ok: false };

/** Parse a whole-number filter box: blank means "no limit", anything but digits is refused. */
export function parseWhole(text: string): Parsed {
  const trimmed = text.trim();
  if (trimmed === "") {
    return { ok: true, value: null };
  }
  return /^\d{1,12}$/.test(trimmed) ? { ok: true, value: Number(trimmed) } : { ok: false };
}

/** Keep the rows that satisfy every filter; a row without the metric is excluded, never counted as zero. */
export function applyFilters(rows: readonly ScanRow[], filters: ScanFilters): ScanRow[] {
  return rows.filter((row) => {
    if (filters.side !== "ALL" && row.side !== filters.side) {
      return false;
    }
    if (filters.stage !== "ALL" && row.stage !== filters.stage) {
      return false;
    }
    if (filters.sector !== "ALL" && row.sector !== filters.sector) {
      return false;
    }
    if (filters.catalyst === "WITH" && row.catalyst === null) {
      return false;
    }
    if (filters.catalyst === "WITHOUT" && row.catalyst !== null) {
      return false;
    }
    if (filters.minTurnoverRupees !== null) {
      if (row.turnoverPaise === null || row.turnoverPaise < filters.minTurnoverRupees * 100n) {
        return false;
      }
    }
    if (filters.maxParticipationPercent !== null) {
      if (
        row.participationPpm === null ||
        row.participationPpm > filters.maxParticipationPercent * 10_000
      ) {
        return false;
      }
    }
    if (filters.maxAbsGapBp !== null) {
      if (row.gapBp === null || Math.abs(row.gapBp) > filters.maxAbsGapBp) {
        return false;
      }
    }
    return true;
  });
}

/** The sectors present, sorted. */
export function sectorsOf(rows: readonly ScanRow[]): string[] {
  return [...new Set(rows.map((row) => row.sector))].sort();
}

/** The total listings the funnel accounts for. */
export function funnelTotal(rows: readonly FunnelRow[]): number {
  return rows.reduce((sum, row) => sum + row.count, 0);
}
